/* eslint-disable */

import { useState, useEffect, useCallback, useRef } from "react";
import { useEcomStore } from "@/context/ecom/EcomContext";
import { useLogin } from "@/context/login/LoginContext";
// import { getTrialFormId } from "../../functions/getTrialForm"
//import { productsEndpoint, checkoutBaseUrl } from "@/lib/ecomConfig"
import { productsEndpoint, checkoutBaseUrl } from "@/lib/config/environments";
import { refreshAuthToken, refreshAuthTokenAsync } from "@/lib/refreshAuthToken";
import { legalEntityData, defaultCurrencies } from "./legalEntities";
import { getCurrency, getLocation, createAnonynousCart, getAccounts, getAnonymousCartData, getRegisteredCartData, mergeAnonymousCart } from "./api";
import { applyCreditProductMapping } from "./lineItemHelpers";
import { pushDataLayerEvent } from "@/components/client/Datalayer/utilities";

/** Module-level cache: countryCode → currency string. Avoids redundant getCurrency API calls. */
const currencyCache = new Map();
const getCurrencyCached = async (countryCode, handleServerError) => {
  if (currencyCache.has(countryCode)) return currencyCache.get(countryCode);
  const currency = await getCurrency(countryCode, handleServerError);
  if (currency) currencyCache.set(countryCode, currency);
  return currency;
};
export const isAuthTokenValid = authTime => {
  if (authTime) {
    // the token for all requests is only valid for 60 minutes after the initial authentication of a user. This checks if the token has expired
    const minutesRemaining = (Date.now() - Number(authTime)) / 60000;
    return minutesRemaining <= 60;
  }
  return false;
};
const formatLineItems = ({
  sku,
  newItems,
  currentItems
}) => {
  const lineItems = newItems?.map((newItem, i) => {
    const currentItem = currentItems?.find(d => d?.sku === newItem?.sku);
    const merged = {
      ...currentItem,
      // ADD imageURL WHICH IS ONLY RETURNED IN INITIAL CART DATA
      ...newItem,
      ...(sku && newItem?.sku === sku ? {
        isMaxThresholdError: false,
        isMinThresholdError: false
      } : {}),
      // RESET THRESHOLD ERRORS IF APPLICABLE
      isLoading: false
    };
    return applyCreditProductMapping(merged);
  });
  return lineItems;
};
const useFlyoutCart = ({
  isAuthenticated,
  disableCartFlyout,
  rawFlyoutCartData,
  ecommerceSettings,
  queryString,
  path,
  // products,
  // isServerError,
  // setServerError,
  // setSubscriptionError,
  client_id,
  redirect_uri,
  code_verifier
  // setProductsRemoved,
  // setCartUpdatedByAPI,
  // setMultiErrorMessage,
}) => {
  const [state, dispatch] = useEcomStore() ?? [];
  const loginCtx = useLogin();
  const [loginState, loginDispatch] = loginCtx ?? [];

  /** Current page URL for OAuth `state`; ref avoids re-running cart effects on every navigation. */
  const pathRef = useRef(path);
  pathRef.current = path;
  const getTIDAuth = useCallback(() => {
    const authTime = loginState?.authTime;
    const token = loginState?.token;
    if (isAuthTokenValid(authTime) && token) {
      return {
        id: token,
        time: Number(authTime)
      };
    }
    return null;
  }, [loginState?.authTime, loginState?.token]);
  const onLogin = useCallback((token_0, authTime_0, idToken, refreshToken) => {
    loginDispatch?.({
      type: "SET_LOGIN",
      payload: {
        token: token_0,
        authTime: authTime_0,
        idToken,
        refreshToken
      }
    });
  }, [loginDispatch]);
  const onLogout = useCallback(() => {
    loginDispatch?.({
      type: "LOG_OUT"
    });
  }, [loginDispatch]);
  const tokenRefreshPromiseRef = useRef(null);
  const getValidToken = async () => {
    if (!loginState?.token || !client_id || !redirect_uri || !code_verifier) return null;
    if (isAuthTokenValid(loginState?.authTime)) return loginState?.token;

    // If a token refresh is already in progress, return the existing promise
    if (tokenRefreshPromiseRef.current) return tokenRefreshPromiseRef.current;

    // Otherwise, start a new token refresh and store the promise
    tokenRefreshPromiseRef.current = (async () => {
      try {
        const authData = await refreshAuthTokenAsync({
          refreshToken: loginState?.refreshToken,
          client_id,
          redirect_uri,
          code_verifier,
          path: pathRef.current
        });
        if (authData?.id) {
          onLogin(authData.id, String(authData.time), authData.idTokenForLogout ?? "", authData.refresh_token ?? "");
          return authData.id;
        }

        // If refresh fails, log out the user
        onLogout();
        return null;
      } finally {
        tokenRefreshPromiseRef.current = null;
      }
    })();
    return tokenRefreshPromiseRef.current;
  };
  const [referrerUrl, setReferrerUrl] = useState("");
  const [isCartFlyoutOpen, setIsCartFlyoutOpen] = useState(false);
  const [userKey, setUserKey] = useState(null);
  const [locationInfo, setLocationInfo] = useState(null);
  const [currencies, setCurrencies] = useState([]);
  const [tokenRefreshing, setTokenRefreshing] = useState(false);
  const mergeJustCompleted = useRef(false);
  // WHEN A USER RETURNS FROM SIGN-IN TO CHECKOUT, WE WANT TO OPEN THE FLYOUT
  // AUTOMATICALLY -- BUT NOT UNTIL AFTER THE COUNTRY-EXCLUSION CHECK BELOW HAS
  // HAD A CHANCE TO REMOVE ANY NOW-EXCLUDED ITEMS, SO THE USER DOESN'T SEE THE
  // CART FLASH OPEN WITH AN ITEM THAT'S ABOUT TO DISAPPEAR.
  const pendingCheckoutFlyoutOpenRef = useRef(false);
  const [productsRemoved, setProductsRemoved] = useState(null);
  const [isCartUpdatedByAPI, setCartUpdatedByAPI] = useState(false);
  const [multiErrorMessage, setMultiErrorMessage] = useState(null);
  const [isSubscriptionError, setSubscriptionError] = useState(false);
  const [isServerError, setServerError] = useState(false);
  const [isAccountError, setIsAccountError] = useState(false);
  const isRegisteredCart = (() => {
    const isOwner = state?.activeAccount?.role === "ACCOUNT_OWNER" || state?.activeAccount?.role === "SECONDARY_ACCOUNT_OWNER";
    const isDVW = state?.activeAccount?.sourceSystems?.includes("DVW");
    return !!(state?.activeAccount?.id && isOwner && isDVW);
  })();
  useEffect(() => {
    setReferrerUrl(encodeURIComponent(`${window.location.origin}${window.location.pathname}`));
  }, []);

  // MERGE ANONYMOUS CART ON ACCOUNT CONFIRMATION
  useEffect(() => {
    const anonymousCartId = localStorage?.getItem("cartId");
    if (!state?.isAccountConfirmed && Array.isArray(state?.accounts) && state?.accounts?.length > 0 && anonymousCartId) {
      const isOwner_0 = state?.activeAccount?.role === "ACCOUNT_OWNER" || state?.activeAccount?.role === "SECONDARY_ACCOUNT_OWNER";
      const ownerAccounts = state?.accounts?.filter(account => account?.role === "ACCOUNT_OWNER" || account?.role === "SECONDARY_ACCOUNT_OWNER");

      // If the active account is an owner and there is only one owner account, we can set the account as confirmed
      if (isOwner_0 && ownerAccounts.length == 1) {
        dispatch({
          type: "SET_ACCOUNT_CONFIRMED",
          payload: true
        });
      } else if (ownerAccounts.length > 1) {
        // If there is an anonymous cart and there are owner accounts, we need to trigger account selection
        dispatch({
          type: "SET_ACCOUNT_CONFIRMATION_REQUIRED",
          payload: true
        });
      }
    }
  }, [state?.accounts, state?.activeAccount, state?.isAccountConfirmed]);
  useEffect(() => {
    const countryCode = !isRegisteredCart ? state?.location?.countryCode : state?.activeAccount?.address?.country ? state?.activeAccount?.address?.country : localStorage?.getItem("country") ? localStorage?.getItem("country") : state?.location?.countryCode;
    if (countryCode) {
      dispatch({
        type: "SET_COUNTRY",
        payload: countryCode
      });
    }
  }, [state?.location?.countryCode, state?.activeAccount?.address?.country, isRegisteredCart, dispatch]);

  // if page has been idling for over an hour and a user opens the flyout and their token is expired, we want to ensure we fresh their token immediately
  useEffect(() => {
    if (isAuthenticated) {
      const activeToken = getTIDAuth();

      // const d = new Date(activeToken?.time)
      // console.log("Token time:", d.toLocaleString())

      if (activeToken?.id) {
        // console.log("Token valid!")
        if (activeToken?.id !== userKey?.id) {
          setUserKey(activeToken);
        }
      } else {
        // console.log("Token refresh required!")
        setTokenRefreshing(true);
        refreshAuthToken(loginState?.refreshToken ?? null, client_id, redirect_uri, code_verifier, pathRef.current, setUserKey, setTokenRefreshing, null, null, onLogin, onLogout);
      }
    }
  }, [isCartFlyoutOpen, state?.cart, isAuthenticated, userKey?.id, onLogin, onLogout, loginState?.refreshToken, client_id, redirect_uri, code_verifier, getTIDAuth]);
  const handleServerError = useCallback(() => {
    setServerError(true);
  }, [setServerError]);
  const handleAccountError = useCallback(() => {
    setIsAccountError(true);
  }, [setIsAccountError]);
  const updateCart = ({
    sku,
    data,
    openFlyout = true
  }) => {
    if (data?.lineItems?.length > 0) {
      const lineItems = formatLineItems({
        sku,
        newItems: data?.lineItems,
        currentItems: state?.cart?.lineItems
      });
      const payload = {
        ...state?.cart,
        ...data,
        lineItems,
        openFlyout
      };
      dispatch({
        type: "UPDATE_CART",
        payload: payload
      });
    } else {
      dispatch({
        type: "UPDATE_CART",
        payload: data
      });
    }
  };
  const updateCartWithError = (sku_0, errorCode) => {
    const lineItems_0 = state?.cart?.lineItems?.map(item => {
      if (item?.sku === sku_0) {
        if (errorCode === "PRODUCTS_1506") {
          return {
            ...item,
            isMaxThresholdError: true,
            isLoading: false
          };
        }
        if (errorCode === "PRODUCTS_1516") {
          return {
            ...item,
            isMinThresholdError: true,
            isLoading: false
          };
        }
      }
      return {
        ...item,
        isLoading: false
      };
    });
    const payload_0 = {
      ...state?.cart,
      lineItems: lineItems_0
    };
    dispatch({
      type: "UPDATE_CART",
      payload: payload_0
    });
  };
  const handleCurrencyChange = async currency => {
    dispatch({
      type: "SET_CURRENCY",
      payload: currency
    });
  };
  useEffect(() => {
    if (disableCartFlyout) return;
    async function initializeLocation() {
      const location = await getLocation();
      if (location?.status === "success") {
        setLocationInfo(location);
        dispatch({
          type: "SET_LOCATION",
          payload: location
        });
      }
      const currencyMatches = location?.countryCode ? legalEntityData.filter(d => d?.countryCode === location?.countryCode) : [];
      const currencies_0 = currencyMatches?.length > 0 ? currencyMatches : defaultCurrencies;
      setCurrencies(currencies_0);
    }
    if (state?.isCartInitialized) {
      initializeLocation();
    }
  }, [disableCartFlyout, state?.isCartInitialized, dispatch]);
  const setLoading = (sku_1, isLoading = true) => {
    if (!state?.cart?.lineItems) return;
    const lineItems_1 = state?.cart?.lineItems?.map(item_0 => {
      if (item_0?.sku === sku_1) {
        return {
          ...item_0,
          isLoading: isLoading
        };
      }
      return item_0;
    });
    const payload_1 = {
      ...state?.cart,
      lineItems: lineItems_1
    };
    dispatch({
      type: "UPDATE_CART",
      payload: payload_1
    });
  };

  // CHECK ANONYMOUS CART CURRENCY; IF DIFFERENT, CREATE NEW CART
  useEffect(() => {
    const createNewCart = async () => {
      const handleGeolocationError = () => {
        localStorage.removeItem("cartId");

        // adding this to ensure cart icon in nav updates as expected. State was lingering until a page refresh
        dispatch({
          type: "UPDATE_CART",
          payload: null
        });
      };
      const lineItems_2 = state?.cart?.lineItems?.map(item_1 => ({
        sku: item_1?.sku,
        quantity: item_1?.quantity
      }));
      localStorage.removeItem("cartId");
      const cart = await createAnonynousCart(state?.currency, state?.countryCode, lineItems_2, handleGeolocationError, handleServerError);
      if (cart?.id) {
        localStorage.setItem("cartId", cart?.id);
        updateCart({
          sku: null,
          data: cart,
          openFlyout: false
        });
      }
    };
    const anonymousCartId_0 = localStorage?.getItem("cartId");
    if (anonymousCartId_0 && state?.cart?.id && state?.currency && state?.currency !== state?.cart?.totalNet?.currencyCode) {
      if (state?.cart?.lineItems && state?.cart?.lineItems?.length > 0) {
        createNewCart();
      } else {
        localStorage.removeItem("cartId");
      }
    }
  }, [state?.currency, state?.cart, state?.countryCode]);
  useEffect(() => {
    if (disableCartFlyout) return;
    const setCurrency = async () => {
      let currency_0 = undefined;
      if (state?.isAccountConfirmed && isRegisteredCart) {
        if (state?.activeAccount?.address?.country) {
          localStorage.setItem("country", state?.activeAccount?.address?.country);
        }

        // SET CURRENCY
        if (state?.activeAccount?.currency) {
          currency_0 = state?.activeAccount?.currency;
          localStorage.setItem("currency", state?.activeAccount?.currency);
        }
      }

      // IF CURRENCY IS NOT SET, GET CURRENCY FROM LOCATION
      if (!currency_0 && state?.location?.countryCode) {
        currency_0 = await getCurrencyCached(state?.location?.countryCode, handleServerError);
      }
      if (currency_0) {
        dispatch({
          type: "SET_CURRENCY",
          payload: currency_0
        });
      }
    };
    setCurrency();
  }, [disableCartFlyout, state?.isAccountConfirmed, isRegisteredCart, state?.activeAccount?.id, state?.activeAccount?.address?.country, state?.activeAccount?.currency, state?.location?.countryCode, handleServerError, dispatch]);
  useEffect(() => {
    const fetchMyAccounts = async userKey_0 => {
      let myAccount = null;
      dispatch({
        type: "SET_ACCOUNTS_LOADING",
        payload: true
      });
      let myAccounts = await getAccounts(userKey_0, handleAccountError, loginState?.token);
      dispatch({
        type: "SET_ACCOUNTS_LOADING",
        payload: false
      });
      if (Array.isArray(myAccounts) && myAccounts?.length > 0) {
        const ownerAccounts_0 = myAccounts.filter(account_0 => {
          return account_0?.role === "ACCOUNT_OWNER";
        }).toSorted((a, b) => {
          if (a?.name?.toLowerCase() < b?.name?.toLowerCase()) return -1;
          if (a?.name?.toLowerCase() > b?.name?.toLowerCase()) return 1;
          return 0;
        });
        const secondaryOwnerAccounts = myAccounts.filter(account_1 => {
          return account_1?.role === "SECONDARY_ACCOUNT_OWNER";
        }).toSorted((a_0, b_0) => {
          if (a_0?.name?.toLowerCase() < b_0?.name?.toLowerCase()) return -1;
          if (a_0?.name?.toLowerCase() > b_0?.name?.toLowerCase()) return 1;
          return 0;
        });
        const adminAccounts = myAccounts.filter(account_2 => {
          return account_2?.role === "COMPANY_ADMIN";
        }).toSorted((a_1, b_1) => {
          if (a_1?.name?.toLowerCase() < b_1?.name?.toLowerCase()) return -1;
          if (a_1?.name?.toLowerCase() > b_1?.name?.toLowerCase()) return 1;
          return 0;
        });
        const userAccounts = myAccounts.filter(account_3 => {
          return account_3?.role === "PRODUCT_USER";
        }).toSorted((a_2, b_2) => {
          if (a_2?.name?.toLowerCase() < b_2?.name?.toLowerCase()) return -1;
          if (a_2?.name?.toLowerCase() > b_2?.name?.toLowerCase()) return 1;
          return 0;
        });
        const purchasingAccounts = [...ownerAccounts_0, ...secondaryOwnerAccounts];
        myAccounts = [...purchasingAccounts, ...adminAccounts, ...userAccounts];

        // IF THERE IS A DEFAULT ACCOUNT, SET THAT AS THE ACTIVE ACCOUNT
        myAccount = myAccounts?.find(account_4 => {
          return account_4?.isDefaultAccount;
        }) ?? myAccounts[0];
        const params = new Proxy(new URLSearchParams(window.location.search), {
          get: (searchParams, prop) => searchParams.get(prop)
        });
        let accountFromUrlParam;
        if (params?.account) {
          accountFromUrlParam = myAccounts?.find(d_0 => params?.account === d_0?.id);
        }
        if (accountFromUrlParam && accountFromUrlParam?.id) {
          if (accountFromUrlParam?.role === "ACCOUNT_OWNER" || accountFromUrlParam?.role === "SECONDARY_ACCOUNT_OWNER") {
            localStorage?.removeItem("cartId");
          }
          myAccount = accountFromUrlParam;
          sessionStorage.setItem("accountId", accountFromUrlParam?.id);
        } else if (sessionStorage.getItem("accountId")) {
          const activeAccount = myAccounts.find(account_5 => {
            return account_5?.id == sessionStorage.getItem("accountId");
          });
          if (activeAccount?.id) {
            myAccount = activeAccount;
          }
        }
      }

      // ACCOUNTS WILL BE SET TO NULL IF AN ERROR OCCURS
      dispatch({
        type: "SET_ACCOUNTS",
        payload: myAccounts
      });
      dispatch({
        type: "SET_ACCOUNT_ID",
        payload: myAccount?.id
      });
      dispatch({
        type: "SET_ACTIVE_ACCOUNT",
        payload: myAccount
      });
    };
    if (disableCartFlyout === false && isAuthenticated && loginState?.token) {
      fetchMyAccounts(loginState.token);
    }
  }, [disableCartFlyout, isAuthenticated, loginState?.token]);
  const anonymousCartId_1 = typeof window !== "undefined" ? localStorage?.getItem("cartId") : "";
  useEffect(() => {
    const fetchCartData = async (setProductsRemoved_0, setCartUpdatedByAPI_0, setMultiErrorMessage_0) => {
      let data_0 = null;
      const anonymousCartId_2 = localStorage?.getItem("cartId");
      const token_1 = await getValidToken();

      // IF USER IS AUTHENTICATED
      if (token_1) {
        // IF THE USER HAS AN E-COMMERCE ACCOUNT
        if (state?.activeAccount?.id) {
          const isOwner_1 = state?.activeAccount?.role === "ACCOUNT_OWNER" || state?.activeAccount?.role === "SECONDARY_ACCOUNT_OWNER";
          const isDVW_0 = state?.activeAccount?.sourceSystems?.includes("DVW");
          const isRegisteredCart_0 = state?.activeAccount?.id && isOwner_1 && isDVW_0;

          // IF THE USER HAS AN ANONYMOUS CART
          if (anonymousCartId_2) {
            data_0 = await getAnonymousCartData(anonymousCartId_2, handleServerError);

            // MERGE ANONYMOUS CART
            if (data_0 && isDVW_0 && isOwner_1 && state?.isAccountConfirmed) {
              let isSubscriptionError_0 = false;
              const handleSubscriptionError = () => {
                isSubscriptionError_0 = true;
                setSubscriptionError(true);
              };
              const handleGeolocationError_0 = () => {
                setCartUpdatedByAPI_0(true);
              };
              const handleMergeError = data_1 => {
                // console.log("Merge API response", data)

                const mergeWarningForRegionBlockedProduct = data_1?.mergeWarning?.find(warning => {
                  return warning?.key === "REMOVED_SKU";
                });
                const mergeWarningForCurrencyMismatch = data_1?.mergeWarning?.find(warning_0 => {
                  return warning_0?.key === "CURRENCY_MISMATCH";
                });
                const mergeWarningForThresholdExceeded = data_1?.mergeWarning?.find(warning_1 => {
                  return warning_1?.key === "THRESHOLD_EXCEED";
                });
                // ERROR TO REMOVE PROJECT SIGHT FROM ROW USERS ONLY
                if (mergeWarningForRegionBlockedProduct && !mergeWarningForCurrencyMismatch?.key && !mergeWarningForThresholdExceeded?.key) {
                  setProductsRemoved_0({
                    removed: true,
                    products: mergeWarningForRegionBlockedProduct?.value
                  });
                  // ERROR TO REMOVE PROJECT SIGHT FROM ROW USERS AND DISPLAY CURRENCY MISMATCH ERROR
                } else if (mergeWarningForRegionBlockedProduct && mergeWarningForCurrencyMismatch?.key && !mergeWarningForThresholdExceeded?.key) {
                  setMultiErrorMessage_0({
                    currencyMismatch: true,
                    products: mergeWarningForRegionBlockedProduct?.value
                  });
                } else if (mergeWarningForThresholdExceeded?.key && mergeWarningForRegionBlockedProduct && !mergeWarningForCurrencyMismatch?.key) {
                  setMultiErrorMessage_0({
                    thresholdErrorAndRegionError: true,
                    products: mergeWarningForRegionBlockedProduct?.value
                  });
                }
                // ERROR TO REMOVE PROJECT SIGHT FROM ROW USERS, DISPLAY CURRENCY MISMATCH AND THRESHOLD ERRORS
                else if (mergeWarningForCurrencyMismatch?.key && mergeWarningForThresholdExceeded?.key && mergeWarningForRegionBlockedProduct) {
                  setMultiErrorMessage_0({
                    thresholdCurrencyMisMatchAndRegionBlockedErrors: true,
                    products: [mergeWarningForRegionBlockedProduct?.value]
                  });
                  // ERROR FOR CURRENCY MISMATCH
                } else if (mergeWarningForCurrencyMismatch?.key && !mergeWarningForThresholdExceeded?.key && !mergeWarningForRegionBlockedProduct) {
                  setMultiErrorMessage_0({
                    currencyMismatchErrorOnly: true
                  });
                  // ERROR FOR THRESHOLD AND CURRENCY MISMATCH ONLY - NEED PROPER ERROR MESSAGING
                } else if (mergeWarningForCurrencyMismatch?.key && mergeWarningForThresholdExceeded?.key && !mergeWarningForRegionBlockedProduct) {
                  setMultiErrorMessage_0({
                    currencyMismatchErrorAndThresholdError: true
                  });
                  // ERROR FOR ONLY THRESHOLD ONLY
                } else if (mergeWarningForThresholdExceeded?.key && !mergeWarningForCurrencyMismatch?.key && !mergeWarningForRegionBlockedProduct) {
                  setMultiErrorMessage_0({
                    thresholdErrorOnly: true
                  });
                }
              };
              // ASSIGN ANONYMOUS CART TO ACCOUNT
              mergeJustCompleted.current = true;
              data_0 = await mergeAnonymousCart(token_1, state?.activeAccount?.id, anonymousCartId_2, handleSubscriptionError, handleGeolocationError_0, handleMergeError, handleServerError);

              // AFTER MERGE, REMOVE ANONYMOUS CART
              localStorage.removeItem("cartId");
              if (isSubscriptionError_0) {
                console.log("Subscription error occurred during cart merge");
                data_0 = await getRegisteredCartData(token_1, state?.activeAccount?.id, handleServerError);
              }
            } else if (!data_0 && state?.isAccountConfirmed) {
              console.log("No anonymous cart found, fetching registered cart data");
              // IF THE ANONYMOUS CART IS NOT FOUND, GET THE REGISTERED CART
              data_0 = await getRegisteredCartData(token_1, state?.activeAccount?.id, handleServerError);
            }

            // HAS AN E-COMMERCE ACCOUNT
            // NO ANONYMOUS CART
          } else {
            // WITHOUT THIS, THE ADD TO CART BUTTON ON PDP STAYS DISABLED FOREVER
            if (!isRegisteredCart_0) {
              data_0 = null;
            } else {
              console.log("Fetching registered cart data");
              data_0 = await getRegisteredCartData(token_1, state?.activeAccount?.id, handleServerError);
            }
          }

          // NO E-COMMERCE ACCOUNT
          // HAS AN ANONYMOUS CART
        } else if (anonymousCartId_2) {
          console.log("Fetching anonymous cart data");
          data_0 = await getAnonymousCartData(anonymousCartId_2, handleServerError);
        }

        // IF USER IS NOT AUTHENTICATED
      } else if (anonymousCartId_2) {
        console.log("Fetching anonymous cart data");
        data_0 = await getAnonymousCartData(anonymousCartId_2, handleServerError);
      }
      let payload_2 = null;
      if (data_0) {
        const lineItems_3 = formatLineItems({
          sku: null,
          newItems: data_0?.lineItems,
          currentItems: state?.cart?.lineItems
        });
        payload_2 = {
          openFlyout: false,
          ...data_0,
          lineItems: lineItems_3
        };
      }
      dispatch({
        type: "UPDATE_CART",
        payload: payload_2
      });
      dispatch({
        type: "SET_CART_INITIALIZED",
        payload: true
      });

      // HAS SIGNED IN TO CHECKOUT BEEN INITIATED?
      const isEcommercePage = /\/products\/[^/]+\/buy(\/|$)/.test(path);
      const checkout = sessionStorage.getItem("checkout");
      if (!isEcommercePage && checkout === "true" || isEcommercePage && checkout === "true" && state?.isAccountConfirmed) {
        sessionStorage.removeItem("checkout");
        pendingCheckoutFlyoutOpenRef.current = true;
      }
    };

    // DELAY LOADING THE CART UNTIL ONE OF THE TWO ARE TRUE:

    // A) IF AUTHENTICATED, ACCOUNT MUST BE CONFIRMED
    const isAuthAndConfirmed = isAuthenticated && state?.isAccountConfirmed;

    // B) IF NOT AUTHENTICATED, AUTH CHECK MUST BE COMPLETE
    const isAuthChecked = !isAuthenticated && state?.isAuthCheckComplete;

    // IF A MERGE JUST COMPLETED, SKIP THE RE-FETCH TRIGGERED BY anonymousCartId CLEARING
    if (mergeJustCompleted.current) {
      mergeJustCompleted.current = false;
      return;
    }
    const isEcommercePage_0 = /\/products\/[^/]+\/buy(\/|$)/.test(path);
    if (disableCartFlyout === false && state?.isAuthCheckComplete && (isAuthAndConfirmed || isAuthChecked || !isEcommercePage_0)) {
      dispatch({
        type: "SET_CART_INITIALIZED",
        payload: false
      });
      fetchCartData(setProductsRemoved, setCartUpdatedByAPI, setMultiErrorMessage);
    }
  }, [disableCartFlyout, isAuthenticated, state?.isAuthCheckComplete, state?.isAccountConfirmed, path, state?.activeAccount?.id, dispatch, anonymousCartId_1, handleServerError]);

  // WHEN THE CART HAS CHANGED, OPEN THE FLYOUT
  useEffect(() => {
    if (state?.cart && state?.cart?.openFlyout !== false) {
      setIsCartFlyoutOpen(true);
    }
  }, [state?.cart]);
  const handleSignIn = async () => {
    const path_0 = `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
    sessionStorage.setItem("checkout", "true");
    window.open(`${queryString}${path_0}`, "_self");
  };
  const isAcountOwner = state?.activeAccount?.role === "ACCOUNT_OWNER" || state?.activeAccount?.role === "SECONDARY_ACCOUNT_OWNER";
  const purchaseableAccounts = state?.accounts?.filter(account_6 => account_6?.role === "ACCOUNT_OWNER" || account_6?.role === "SECONDARY_ACCOUNT_OWNER");
  const cartSettings = {
    cartTitle: rawFlyoutCartData?.cartTitle || "My Cart",
    subtotalLabel: rawFlyoutCartData?.subtotalLabel || "Subtotal",
    productsTitle: rawFlyoutCartData?.productsTitle || "Your items",
    productsRelatedTitle: rawFlyoutCartData?.productsRelatedTitle || "You may also like",
    addedToCartMessage: rawFlyoutCartData?.addedToCartMessage || "{Product Name} was added to cart",
    removeFromCartMessage: rawFlyoutCartData?.removeFromCartMessage || "{Product Name} was removed from the cart",
    emptyCartTitle: rawFlyoutCartData?.emptyCartTitle || "Your cart is currently empty.",
    emptyCartMessage: rawFlyoutCartData?.emptyCartMessage || "There is nothing in your cart. Let's add some products.",
    errorMessageForMaxQuantity: rawFlyoutCartData?.errorMessageForMaxQuantity || "The quantity selected exceeds the maximum allowed for this transaction. Please contact sales to complete your order.",
    errorMessageForMinQuantity: rawFlyoutCartData?.errorMessageForMinQuantity || "Quantity must be at least 1",
    viewCartBtnLabel: rawFlyoutCartData?.viewCartBtnLabel || "View Cart",
    viewCartBtnUrl: null,
    checkoutBtnLabel: rawFlyoutCartData?.checkoutBtnLabel,
    checkoutBtnUrl:
    // UNPROVISIONED OWNER
    state?.activeAccount?.id && isAcountOwner && !isRegisteredCart ? `${checkoutBaseUrl}/checkout?account=${state?.activeAccount?.id}&cartId=${state?.cart?.id}&referrerUrl=${referrerUrl}` :
    // REGISTERED CART
    state?.activeAccount?.id && isRegisteredCart ? `${checkoutBaseUrl}/checkout?account=${state?.activeAccount?.id}&referrerUrl=${referrerUrl}` :
    // PRODUCT USER/COMPANY ADMIN/ETC.
    state?.activeAccount?.id && !isAcountOwner ? `${checkoutBaseUrl}/checkout?cartId=${state?.cart?.id}&referrerUrl=${referrerUrl}` :
    // NO E-COMMERCE ACCOUNT
    state?.cart?.id ? `${checkoutBaseUrl}/account-setup?cartId=${state?.cart?.id}&referrerUrl=${referrerUrl}` : null,
    continueShoppingBtnLabel: rawFlyoutCartData?.continueShoppingBtnLabel || "Continue Shopping",
    removeBtnLabel: rawFlyoutCartData?.removeBtnLabel || "Remove",
    signInBtnLabel: rawFlyoutCartData?.signInBtnLabel || "Sign In to Checkout",
    signInBtnMethod: handleSignIn,
    finePrint: rawFlyoutCartData?.finePrint?.childMarkdownRemark?.html,
    cartMenuTitle: rawFlyoutCartData?.cartMenuTitle || "Cart",
    cartMenuViewLabel: rawFlyoutCartData?.cartMenuViewLabel || "View Cart",
    currencyListTitle: rawFlyoutCartData?.currencyListTitle || "Currency",
    currencyListText: rawFlyoutCartData?.currencyListText?.text || "",
    currencyDefaultLabel: rawFlyoutCartData?.currencyDefaultLabel || "Default",
    currencySubmitLabel: rawFlyoutCartData?.currencySubmitLabel || "OK",
    currencyCancelLabel: rawFlyoutCartData?.currencyCancelLabel || "Cancel",
    account: state?.activeAccount,
    isSwitchAccountActive: purchaseableAccounts?.length > 1,
    switchAccountLabel: rawFlyoutCartData?.switchAccountLabel || "Switch Account",
    quantityLabel: ecommerceSettings?.quantityLabel,
    onAccountClick: () => {
      dispatch({
        type: "SET_ACCOUNT_CHANGE_REQUESTED",
        payload: true
      });
    },
    belowMinimumLabel: ecommerceSettings?.belowMinimumLabel,
    aboveMaximumLabel: ecommerceSettings?.aboveMaximumLabel,
    minimumMaximumLabel: ecommerceSettings?.minimummaximumLabel,
    creditExpirationNoticeLabel: ecommerceSettings?.creditExpirationNoticeLabel
  };

  // UPDATE CART STATE
  const handleIsCartFlyoutOpen = isCartFlyoutOpen_0 => {
    setIsCartFlyoutOpen(isCartFlyoutOpen_0);
  };
  const handleCloseDialogs = () => {
    setSubscriptionError(false);
    setProductsRemoved(null);
    setCartUpdatedByAPI(false);
    setMultiErrorMessage(null);
    setIsAccountError(false);
    setServerError(false);
  };
  const handleCartChangeQuantity = async (id, sku_2, name, quantity) => {
    const token_2 = await getValidToken();
    if (!token_2 && isRegisteredCart) {
      handleServerError();
      return;
    }
    try {
      const url = isRegisteredCart ? `${productsEndpoint}/carts` : `${productsEndpoint}/carts/${state?.cart?.id}`;
      const headers = isRegisteredCart ? {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${token_2}`,
        "x-account-id": state?.activeAccount?.id
      } : {
        "Content-Type": "application/json",
        Accept: "application/json"
      };
      setLoading(sku_2, true);
      const response = await fetch(url, {
        method: isRegisteredCart ? "PATCH" : "PUT",
        headers: headers,
        body: JSON.stringify({
          lineItems: [{
            productSku: sku_2,
            updateAction: quantity > 0 ? "UPDATE_CART_LINEITEM" : "REMOVE_CART_LINEITEM",
            lineitemId: id,
            ...(quantity > 0 && {
              quantity: quantity
            })
          }]
        })
      });
      const data_2 = await response.json();
      if (response.ok) {
        updateCart({
          sku: sku_2,
          data: data_2
        });
        return true; // SUCCESS
      } else if (data_2?.errorCode) {
        updateCartWithError(sku_2, data_2?.errorCode);
        if (data_2.errorCode === "PRODUCTS_1506") {
          return false; // maxQuantityError - this avoids trigger pop up dialog when max quantity is exceeded, we only want the inline error on flyout
        }
        throw new Error(data_2?.errorDescription);
      }
      setLoading(sku_2, false);
      throw new Error("Unable to update cart item quantity");
    } catch (error) {
      console.error(error);
      handleServerError();
      return false; // FAILURE
    }
  };
  const handleCartRemoveItem = async (id_0, sku_3) => {
    const token_3 = await getValidToken();
    if (!token_3 && isRegisteredCart) {
      handleServerError();
      return;
    }
    try {
      const url_0 = isRegisteredCart ? `${productsEndpoint}/carts` : `${productsEndpoint}/carts/${state?.cart?.id}`;
      const headers_0 = isRegisteredCart ? {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${token_3}`,
        "x-account-id": state?.activeAccount?.id
      } : {
        "Content-Type": "application/json",
        Accept: "application/json"
      };
      setLoading(sku_3, true);
      const response_0 = await fetch(url_0, {
        method: isRegisteredCart ? "PATCH" : "PUT",
        headers: headers_0,
        body: JSON.stringify({
          lineItems: [{
            productSku: sku_3,
            updateAction: "REMOVE_CART_LINEITEM",
            lineitemId: id_0
          }]
        })
      });
      const data_3 = await response_0.json();
      if (response_0.ok) {
        if (state?.cart?.lineItems?.length > 0) {
          const itemRemoved = state?.cart?.lineItems?.filter(item_2 => item_2?.sku === sku_3)?.[0];
          pushDataLayerEvent({
            event: "remove_from_cart",
            ecommerce: {
              items: [{
                item_id: itemRemoved?.id,
                item_name: itemRemoved?.name,
                // 'item_brand': '<product brand>',
                item_category: "Software",
                // 'item_category2': '<level 2 category>',
                // 'item_category3': '<level 3 category>',
                // 'item_category4': '<level 4 category>',
                // 'item_category5': '<level 5 category>',
                // 'item_variant': '<product variant>',
                affiliation: "Trimble.com",
                price: itemRemoved?.totalPrice?.amount,
                currency: itemRemoved?.totalPrice?.currencyCode,
                quantity: itemRemoved?.quantity,
                // 'coupon': '<product coupon code>',
                // 'rating': '<product rating>',
                // 'rating_quantity': '<product rating quantity>',
                sku: sku_3
              }]
            }
          }, {
            clearEcommerce: true
          });
        }
        updateCart({
          sku: sku_3,
          data: data_3
        });
        return true; // SUCCESS
      }
      throw new Error(data_3?.errorDescription);
    } catch (error_0) {
      setLoading(sku_3, false);
      console.error(error_0);
      handleServerError();
      return false; // FAILURE
    }
  };

  /**
   * Removes multiple cart line items in a single API call (the products API
   * already accepts an array of `lineItems`), used when items become excluded
   * for the account's country (see `state.skuCountryExclusions`).
   */
  const handleRemoveExcludedCartItems = async items => {
    if (!items?.length) return false;
    const token_4 = await getValidToken();
    if (!token_4 && isRegisteredCart) {
      handleServerError();
      return false;
    }
    try {
      const url_1 = isRegisteredCart ? `${productsEndpoint}/carts` : `${productsEndpoint}/carts/${state?.cart?.id}`;
      const headers_1 = isRegisteredCart ? {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${token_4}`,
        "x-account-id": state?.activeAccount?.id
      } : {
        "Content-Type": "application/json",
        Accept: "application/json"
      };
      items.forEach(item_4 => setLoading(item_4?.sku, true));
      const response_1 = await fetch(url_1, {
        method: isRegisteredCart ? "PATCH" : "PUT",
        headers: headers_1,
        body: JSON.stringify({
          lineItems: items.map(item_5 => ({
            productSku: item_5?.sku,
            updateAction: "REMOVE_CART_LINEITEM",
            lineitemId: item_5?.id
          }))
        })
      });
      const data_4 = await response_1.json();
      if (response_1.ok) {
        items.forEach(item_6 => {
          pushDataLayerEvent({
            event: "remove_from_cart",
            ecommerce: {
              items: [{
                item_id: item_6?.id,
                item_name: item_6?.name,
                item_category: "Software",
                affiliation: "Trimble.com",
                price: item_6?.totalPrice?.amount,
                currency: item_6?.totalPrice?.currencyCode,
                quantity: item_6?.quantity,
                sku: item_6?.sku
              }]
            }
          }, {
            clearEcommerce: true
          });
        });
        updateCart({
          sku: null,
          data: data_4
        });
        setProductsRemoved({
          removed: true,
          products: items.map(item_7 => item_7?.name).filter(Boolean)
        });
        return true; // SUCCESS
      }
      throw new Error(data_4?.errorDescription);
    } catch (error_1) {
      items.forEach(item_3 => setLoading(item_3?.sku, false));
      console.error(error_1);
      handleServerError();
      return false; // FAILURE
    }
  };

  // GUARD AGAINST OVERLAPPING REMOVAL REQUESTS WHILE ONE IS IN FLIGHT
  const isRemovingExcludedItemsRef = useRef(false);

  // OPENS THE FLYOUT IF A SIGN-IN-TO-CHECKOUT REDIRECT IS WAITING ON THE
  // COUNTRY-EXCLUSION CHECK BELOW TO FINISH (SEE pendingCheckoutFlyoutOpenRef)
  const openPendingCheckoutFlyout = () => {
    if (pendingCheckoutFlyoutOpenRef.current) {
      pendingCheckoutFlyoutOpenRef.current = false;
      setIsCartFlyoutOpen(true);
    }
  };

  // IF THE ACCOUNT'S COUNTRY EXCLUDES A SKU ALREADY IN THE CART (e.g. AFTER LOGIN
  // OR SWITCHING ACCOUNTS), REMOVE IT AND NOTIFY THE USER VIA THE EXISTING
  // "PRODUCTS REMOVED" DIALOG. A SIGN-IN-TO-CHECKOUT FLYOUT OPEN IS HELD OFF
  // (VIA pendingCheckoutFlyoutOpenRef) UNTIL THIS CHECK HAS RUN, SO THE USER
  // DOESN'T SEE THE CART FLASH OPEN WITH AN ITEM THAT'S ABOUT TO BE REMOVED.
  useEffect(() => {
    // THE EXCLUSION MAP HASN'T LOADED YET, OR THE CART ISN'T READY -- WAIT.
    if (!state?.isCartInitialized || !state?.skuCountryExclusions) {
      return;
    }
    if (isRemovingExcludedItemsRef.current) {
      return;
    }

    // NOTHING TO CHECK (NO COUNTRY YET, OR AN EMPTY CART) -- THE CHECK IS
    // TRIVIALLY "DONE", SO ANY PENDING CHECKOUT FLYOUT CAN OPEN NOW.
    if (!state?.countryCode || !state?.cart?.lineItems?.length) {
      openPendingCheckoutFlyout();
      return;
    }
    const countryCode_0 = state.countryCode.toUpperCase();
    const excludedItems = state.cart.lineItems.filter(item_8 => {
      const excludedCountries = state.skuCountryExclusions?.[item_8?.sku];
      return Array.isArray(excludedCountries) && excludedCountries.includes(countryCode_0);
    });
    if (excludedItems.length > 0) {
      isRemovingExcludedItemsRef.current = true;
      handleRemoveExcludedCartItems(excludedItems).finally(() => {
        isRemovingExcludedItemsRef.current = false;
        openPendingCheckoutFlyout();
      });
    } else {
      openPendingCheckoutFlyout();
    }
  }, [state?.cart?.lineItems, state?.countryCode, state?.activeAccount?.id, state?.skuCountryExclusions, state?.isCartInitialized]);
  const defaultCurrency = currencies.find(d_1 => d_1?.code === locationInfo?.currency) ? locationInfo?.currency : currencies[0]?.code;
  const flyoutData = {
    cartSettings,
    handleCartChangeQuantity,
    handleCartRemoveItem,
    isCartFlyoutOpen,
    setIsCartFlyoutOpen,
    handleIsCartFlyoutOpen,
    // isCurrencyLocked: isRegisteredCart && typeof state?.activeAccount?.currency === "string",
    isCurrencyLocked: true,
    currencies,
    currency: state?.currency,
    defaultCurrency: defaultCurrency,
    setCurrency: handleCurrencyChange,
    handleCloseDialogs,
    productsRemoved,
    setProductsRemoved,
    isCartUpdatedByAPI,
    setCartUpdatedByAPI,
    multiErrorMessage,
    setMultiErrorMessage,
    isSubscriptionError,
    setSubscriptionError,
    isAccountError,
    setIsAccountError,
    isServerError,
    setServerError
  };
  return flyoutData;
};
export default useFlyoutCart;